import { BodyText, Icons, TableCell, TableRow } from "@/components/ui";
import { capitalize, ensureTrailingZero } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { Observation, type ObservationValue } from "@curai/js-fhir-tools";
import type {
  DxReportViewTestResult,
  ReportResultReferenceRange,
} from "../../network/useDxReportView";

const RESULT_TYPE = {
  NORMAL: "Normal",
  ABNORMAL: "Abnormal",
  LOW: "Low",
  HIGH: "High",
  OTHER: "Other",
};

const getResultTypeAndCriticality = (resultFlag: string) => {
  switch (resultFlag) {
    case Observation.Interpretation.values.HIGH:
      return { resultType: RESULT_TYPE.HIGH, isCritical: false };
    case Observation.Interpretation.values.CRITICAL_HIGH:
      return { resultType: RESULT_TYPE.HIGH, isCritical: true };
    case Observation.Interpretation.values.NORMAL:
      return { resultType: RESULT_TYPE.NORMAL, isCritical: false };
    case Observation.Interpretation.values.LOW:
      return { resultType: RESULT_TYPE.LOW, isCritical: false };
    case Observation.Interpretation.values.CRITICAL_LOW:
      return { resultType: RESULT_TYPE.LOW, isCritical: true };
    case Observation.Interpretation.values.ABNORMAL:
      return { resultType: RESULT_TYPE.ABNORMAL, isCritical: false };
    case Observation.Interpretation.values.CRITICAL_ABNORMAL:
      return { resultType: RESULT_TYPE.ABNORMAL, isCritical: true };
    default:
      return { resultType: RESULT_TYPE.OTHER, isCritical: false };
  }
};

const renderResultValueDecoration = (
  resultType: string,
  isCritical: boolean,
) => {
  const arrowProps = {
    width: 7,
    height: 20,
    viewBox: "6 4 12 12",
    strokeWidth: "2.5px",
  };
  if (resultType === RESULT_TYPE.NORMAL || resultType === RESULT_TYPE.OTHER) {
    return null;
  }

  if (resultType === RESULT_TYPE.ABNORMAL) {
    return <Icons.Dot className="inline-block stroke-error" />;
  }

  const indicator =
    resultType === RESULT_TYPE.HIGH ? (
      <Icons.MoveUp
        {...arrowProps}
        className={cn("inline-block", isCritical && "stroke-error")}
      />
    ) : (
      <Icons.MoveDown
        {...arrowProps}
        className={cn("inline-block", isCritical && "stroke-error")}
      />
    );

  if (isCritical) {
    return (
      <>
        {indicator}
        {indicator}
      </>
    );
  }
  return indicator;
};

const renderResultValue = (
  rawResultValue: ObservationValue | undefined,
  resultType: string,
  isCritical: boolean,
) => {
  const useErrorText = isCritical || resultType === RESULT_TYPE.ABNORMAL;
  let resultValue = Observation.getNormalizedObservationDisplay(rawResultValue);

  if (!Number.isNaN(Number(resultValue))) {
    resultValue = ensureTrailingZero(resultValue);
  }

  return (
    <div className="relative">
      <div className="absolute top-1/2 -translate-y-1/2">
        {renderResultValueDecoration(resultType, isCritical)}
      </div>
      <BodyText
        size="sm"
        textColor={useErrorText ? "error" : "primary"}
        bold={isCritical}
        className="ml-[20px]"
      >
        {resultValue}
      </BodyText>
    </div>
  );
};

const renderResultRange = (rangeObject?: ReportResultReferenceRange) => {
  const rangeLow = rangeObject?.rangeLow;
  const rangeHigh = rangeObject?.rangeHigh;

  if (!rangeLow && !rangeHigh) {
    return "Not available";
  }

  if (rangeLow && rangeHigh) {
    return `${ensureTrailingZero(rangeLow.toString())} - ${ensureTrailingZero(rangeHigh.toString())}`;
  }
  if (rangeLow) {
    return `${ensureTrailingZero(rangeLow.toString())}`;
  }
  if (rangeHigh) {
    return `${ensureTrailingZero(rangeHigh.toString())}`;
  }
};

const renderResultFlag = (resultType: string, isCritical: boolean) => {
  let flagText = "";
  if (isCritical) {
    flagText = "Critical";
  } else if (resultType === RESULT_TYPE.OTHER) {
    flagText = "";
  } else {
    flagText = resultType;
  }

  const isAbnormal = resultType === RESULT_TYPE.ABNORMAL;

  return (
    <BodyText
      textColor={isCritical || isAbnormal ? "error" : null}
      bold={isCritical}
      size="sm"
    >
      {flagText}
    </BodyText>
  );
};

export const Finding = ({
  testResult,
  labLocation,
  showSeparator = true,
}: {
  testResult: DxReportViewTestResult;
  labLocation: number | null;
  showSeparator: boolean;
}) => {
  const { resultType, isCritical } = getResultTypeAndCriticality(
    testResult.resultFlag || "",
  );

  return (
    <TableRow key={testResult.curaiObservationId} showSeparator={showSeparator}>
      <TableCell colSpan={1}>
        <BodyText data-testid="test-result-name" size="sm">
          {testResult.resultName}
        </BodyText>
      </TableCell>
      <TableCell colSpan={1}>
        <BodyText data-testid="test-result-status" size="sm">
          {capitalize(testResult.resultStatus)}
        </BodyText>
      </TableCell>
      <TableCell>
        {renderResultValue(
          testResult.resultRawValue as ObservationValue,
          resultType,
          isCritical,
        )}
      </TableCell>
      <TableCell data-testid="test-result-flag">
        {renderResultFlag(resultType, isCritical)}
      </TableCell>
      <TableCell data-testid="test-result-range" className="whitespace-nowrap">
        <BodyText size="sm">
          {renderResultRange(testResult.resultReferenceRange)}
        </BodyText>
      </TableCell>
      <TableCell data-testid="test-result-lab-location">
        <BodyText size="sm">{labLocation || "Not available"}</BodyText>
      </TableCell>
    </TableRow>
  );
};
